import * as THREE from 'three';

// Geometries and the static materials are shared by every explosion.
const GEOMETRY = {
  ember: new THREE.TetrahedronGeometry(0.35, 0),
  smoke: new THREE.IcosahedronGeometry(1, 0),
  flash: new THREE.IcosahedronGeometry(2, 1),
  // Flat rune ring lying on the ground
  ring: new THREE.RingGeometry(1.6, 2, 24, 1).rotateX(-Math.PI / 2),
  runes: new THREE.RingGeometry(2.3, 2.6, 8, 1, 0, Math.PI * 2).rotateX(-Math.PI / 2)
};

const EMBER_COLORS = [0xffd36b, 0xffb03a, 0xff8a1f, 0xff6a10, 0xfff0b0];
const SMOKE_COLOR = 0x5a4e46;

function glowMaterial(color, opacity = 1) {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });
}

export class Explosion {
  constructor(scene, position, onComplete) {
    this.scene = scene;
    this.position = position.clone();
    this.onComplete = onComplete;
    this.particles = [];
    this.smoke = [];
    this.startTime = performance.now();
    this.lastTime = this.startTime;
    this.duration = 900; // 900ms burst, smoke lingers to the end
    this.isComplete = false;
    this.create();
  }

  create() {
    // Embers and sparks flung outward
    const particleCount = 22;
    for (let i = 0; i < particleCount; i++) {
      const color = EMBER_COLORS[Math.floor(Math.random() * EMBER_COLORS.length)];
      const particle = new THREE.Mesh(GEOMETRY.ember, glowMaterial(color));
      particle.position.copy(this.position);
      particle.scale.setScalar(0.6 + Math.random() * 0.9);
      particle.userData.baseScale = particle.scale.x;

      // Random velocity direction (explosion outward)
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 8 + Math.random() * 14;

      particle.userData.velocity = new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.abs(Math.cos(phi)) * speed * 0.8 + Math.random() * 6, // Bias upward
        Math.sin(phi) * Math.sin(theta) * speed
      );

      particle.userData.rotationSpeed = new THREE.Vector3(
        (Math.random() - 0.5) * 12,
        (Math.random() - 0.5) * 12,
        (Math.random() - 0.5) * 12
      );

      this.particles.push(particle);
      this.scene.add(particle);
    }

    // A few puffs of smoke that rise and swell
    for (let i = 0; i < 5; i++) {
      const puff = new THREE.Mesh(GEOMETRY.smoke, new THREE.MeshLambertMaterial({
        color: SMOKE_COLOR,
        flatShading: true,
        transparent: true,
        opacity: 0,
        depthWrite: false
      }));
      puff.position.copy(this.position).add(new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        Math.random() * 1.2,
        (Math.random() - 0.5) * 2
      ));
      puff.rotation.set(Math.random() * 3, Math.random() * 3, Math.random() * 3);
      puff.userData.rise = 2 + Math.random() * 2.5;
      puff.userData.size = 0.9 + Math.random() * 0.8;
      this.smoke.push(puff);
      this.scene.add(puff);
    }

    // Central warm flash (additive, so no light needed)
    this.flash = new THREE.Mesh(GEOMETRY.flash, glowMaterial(0xffc66b));
    this.flash.position.copy(this.position);
    this.scene.add(this.flash);

    // Brief rune circle flaring on the ground beneath the burst
    this.ring = new THREE.Group();
    this.ring.position.set(this.position.x, 0.1, this.position.z);
    this.ring.add(new THREE.Mesh(GEOMETRY.ring, glowMaterial(0xffa640, 0.8)));
    this.ring.add(new THREE.Mesh(GEOMETRY.runes, glowMaterial(0xffd36b, 0.6)));
    this.ring.visible = this.position.y < 6; // skip for bursts high in the air
    this.scene.add(this.ring);
  }

  update() {
    if (this.isComplete) return false;

    const now = performance.now();
    const elapsed = now - this.startTime;
    const progress = Math.min(elapsed / this.duration, 1);

    if (progress >= 1) {
      this.remove();
      this.isComplete = true;
      if (this.onComplete) {
        this.onComplete();
      }
      return false;
    }

    const deltaTime = Math.min((now - this.lastTime) / 1000, 0.05);
    this.lastTime = now;

    // Embers burn out over the first ~70% of the effect
    const emberLife = Math.min(progress / 0.7, 1);
    for (const particle of this.particles) {
      particle.position.addScaledVector(particle.userData.velocity, deltaTime);

      // Gravity and a little air drag
      particle.userData.velocity.y -= 22 * deltaTime;
      particle.userData.velocity.multiplyScalar(1 - 1.5 * deltaTime);

      particle.rotation.x += particle.userData.rotationSpeed.x * deltaTime;
      particle.rotation.y += particle.userData.rotationSpeed.y * deltaTime;
      particle.rotation.z += particle.userData.rotationSpeed.z * deltaTime;

      particle.material.opacity = 1 - emberLife;
      particle.scale.setScalar(particle.userData.baseScale * (1 - emberLife * 0.7));
    }

    // Smoke fades in after the flash, rises and swells, then thins out
    for (const puff of this.smoke) {
      puff.position.y += puff.userData.rise * deltaTime;
      puff.rotation.y += 0.6 * deltaTime;
      puff.scale.setScalar(puff.userData.size * (0.6 + progress * 1.6));
      puff.material.opacity = 0.55 * Math.min(1, progress * 4) * (1 - progress);
    }

    if (this.flash) {
      this.flash.material.opacity = Math.max(0, 1 - progress * 4);
      this.flash.scale.setScalar(0.6 + progress * 3);
    }

    if (this.ring) {
      const ringLife = Math.min(progress / 0.6, 1);
      this.ring.scale.setScalar(0.6 + ringLife * 1.4);
      this.ring.rotation.y += 1.5 * deltaTime;
      this.ring.children[0].material.opacity = 0.8 * (1 - ringLife);
      this.ring.children[1].material.opacity = 0.6 * (1 - ringLife);
    }

    return true;
  }

  remove() {
    // Shared geometries stay alive; only per-explosion materials are disposed
    for (const particle of this.particles) {
      this.scene.remove(particle);
      particle.material.dispose();
    }
    this.particles = [];

    for (const puff of this.smoke) {
      this.scene.remove(puff);
      puff.material.dispose();
    }
    this.smoke = [];

    if (this.flash) {
      this.scene.remove(this.flash);
      this.flash.material.dispose();
      this.flash = null;
    }

    if (this.ring) {
      this.scene.remove(this.ring);
      for (const child of this.ring.children) {
        child.material.dispose();
      }
      this.ring = null;
    }
  }
}
